import type { ArraySchema, EnumSchema, JsonSchema, ObjectSchema, SchemaDef, SchemaProperty } from '../types/schema';
import type { WizardAnswers, WizardConfig, WizardQuestion } from '../types/wizard';
import { createId } from './modelUtils';
import { normalizeSchema, SCHEMA_URI } from './schemaUtils';

const toString = (value: unknown) => (typeof value === 'string' ? value : '');
const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const uniqueStrings = (values: string[], fallback?: string) => {
  const cleaned = values.map((value) => value.trim()).filter(Boolean);
  const unique = Array.from(new Set(cleaned));
  if (unique.length === 0 && fallback) {
    unique.push(fallback);
  }
  return unique;
};

const findQuestionInList = (questions: WizardQuestion[], id: string): WizardQuestion | null => {
  for (const question of questions) {
    if (question.id === id) {
      return question;
    }
    if (question.questions) {
      const nested = findQuestionInList(question.questions, id);
      if (nested) {
        return nested;
      }
    }
  }
  return null;
};

const findQuestionById = (config: WizardConfig | undefined, id: string): WizardQuestion | null => {
  if (!config) {
    return null;
  }
  for (const step of config.steps) {
    const found = findQuestionInList(step.questions, id);
    if (found) {
      return found;
    }
  }
  return null;
};

const findRepeatSubQuestion = (
  config: WizardConfig | undefined,
  groupId: string,
  subId: string
): WizardQuestion | null => {
  const group = findQuestionById(config, groupId);
  if (!group?.questions) {
    return null;
  }
  return findQuestionInList(group.questions, subId);
};

type DefBuilder = {
  defs: Record<string, SchemaDef>;
  typeIds: Record<string, string>;
  enumIds: Record<string, string>;
};

const createType = (builder: DefBuilder, name: string, description: string) => {
  if (builder.typeIds[name]) {
    return builder.typeIds[name];
  }
  const id = createId();
  builder.typeIds[name] = id;
  builder.defs[id] = {
    type: 'object',
    title: name,
    description,
    properties: {},
    required: [],
  };
  return id;
};

const createEnum = (builder: DefBuilder, name: string, values: string[]) => {
  if (builder.enumIds[name]) {
    return builder.enumIds[name];
  }
  const id = createId();
  builder.enumIds[name] = id;
  builder.defs[id] = {
    type: 'string',
    title: name,
    oneOf: values.map((value) => ({ const: value, title: value })),
  } as EnumSchema;
  return id;
};

const addField = (
  builder: DefBuilder,
  typeId: string,
  name: string,
  schema: SchemaProperty,
  required = false
) => {
  const target = builder.defs[typeId] as ObjectSchema;
  if (!target.properties) {
    target.properties = {};
  }
  if (target.properties[name]) {
    return;
  }
  target.properties[name] = schema;
  if (required) {
    const requiredList = new Set(target.required ?? []);
    requiredList.add(name);
    target.required = Array.from(requiredList);
  }
};

const buildString = (): SchemaProperty => ({ type: 'string' });
const buildNumber = (): SchemaProperty => ({ type: 'number' });
const buildInteger = (): SchemaProperty => ({ type: 'integer' });

const buildEnumRef = (enumId: string): SchemaProperty => ({ $ref: `#/$defs/${enumId}` });
const buildTypeRef = (typeId: string): SchemaProperty => ({ $ref: `#/$defs/${typeId}` });
const buildReference = (typeId: string): SchemaProperty => ({ type: 'string', 'x-refTypeId': typeId });

const buildArray = (items: SchemaProperty): ArraySchema => ({
  type: 'array',
  items,
});

const addEntityBaseFields = (builder: DefBuilder, typeId: string) => {
  addField(builder, typeId, 'id', buildString(), true);
  addField(builder, typeId, 'createdAt', buildString(), false);
};

export const generateGameSchemaFromWizard = (
  answers: WizardAnswers,
  config?: WizardConfig
): JsonSchema => {
  const builder: DefBuilder = { defs: {}, typeIds: {}, enumIds: {} };
  const enabledModules = new Set(toStringArray(answers['core.modules']));

  const projectMetaId = createType(builder, 'ProjectMeta', 'Project metadata and configuration.');
  const genreOptions = uniqueStrings(findQuestionById(config, 'project.genre')?.options ?? []);
  const platformOptions = uniqueStrings(findQuestionById(config, 'project.platforms')?.options ?? []);

  if (genreOptions.length > 0) {
    const genreEnumId = createEnum(builder, 'ProjectGenre', genreOptions);
    addField(builder, projectMetaId, 'genre', buildEnumRef(genreEnumId), true);
  } else {
    addField(builder, projectMetaId, 'genre', buildString(), true);
  }

  if (platformOptions.length > 0) {
    const platformEnumId = createEnum(builder, 'Platform', platformOptions);
    addField(builder, projectMetaId, 'platforms', buildArray(buildEnumRef(platformEnumId)), true);
  } else {
    addField(builder, projectMetaId, 'platforms', buildArray(buildString()), true);
  }

  addField(builder, projectMetaId, 'name', buildString(), true);

  const needsPlayer =
    enabledModules.has('players') ||
    enabledModules.has('clans') ||
    enabledModules.has('leaderboards') ||
    enabledModules.has('matchmaking') ||
    enabledModules.has('auction') ||
    enabledModules.has('chat');

  const ensurePlayerType = () => {
    const playerId = createType(builder, 'Player', 'Player profile and progression.');
    addEntityBaseFields(builder, playerId);
    addField(builder, playerId, 'displayName', buildString(), false);
    if (enabledModules.has('players')) {
      if (answers['players.progression'] === true) {
        addField(builder, playerId, 'level', buildInteger(), false);
        addField(builder, playerId, 'experience', buildInteger(), false);
      }
      if (answers['players.profile'] === true) {
        const profileId = createType(builder, 'PlayerProfile', 'Extended player profile data.');
        addField(builder, profileId, 'bio', buildString(), false);
        addField(builder, profileId, 'avatarUrl', buildString(), false);
        addField(builder, profileId, 'region', buildString(), false);
        addField(builder, playerId, 'profile', buildTypeRef(profileId), false);
      }
    }
    return playerId;
  };

  const playerTypeId = needsPlayer ? ensurePlayerType() : null;

  if (enabledModules.has('economy')) {
    const currencyItems = Array.isArray(answers['economy.currencies'])
      ? (answers['economy.currencies'] as Array<Record<string, unknown>>)
      : [];
    const currencyCodes = uniqueStrings(
      currencyItems.map((item) => toString(item.code)),
      'GOLD'
    );
    const currencyEnumId = createEnum(builder, 'CurrencyCode', currencyCodes);

    const kindQuestion = findRepeatSubQuestion(config, 'economy.currencies', 'kind');
    const kindOptions = uniqueStrings(kindQuestion?.options ?? []);
    const kindEnumId = kindOptions.length > 0 ? createEnum(builder, 'CurrencyKind', kindOptions) : null;

    const balanceId = createType(builder, 'WalletBalance', 'Currency balance entry.');
    addField(builder, balanceId, 'currency', buildEnumRef(currencyEnumId), true);
    addField(builder, balanceId, 'amount', buildNumber(), true);
    if (kindEnumId) {
      addField(builder, balanceId, 'kind', buildEnumRef(kindEnumId), false);
    }

    const walletId = createType(builder, 'Wallet', 'Player wallet balances.');
    addEntityBaseFields(builder, walletId);
    addField(builder, walletId, 'balances', buildArray(buildTypeRef(balanceId)), true);
    if (playerTypeId) {
      addField(builder, walletId, 'ownerId', buildReference(playerTypeId), false);
      addField(builder, playerTypeId, 'wallet', buildTypeRef(walletId), false);
    }
  }

  if (enabledModules.has('inventory')) {
    const itemDefId = createType(builder, 'ItemDefinition', 'Catalog entry for items.');
    addEntityBaseFields(builder, itemDefId);
    addField(builder, itemDefId, 'name', buildString(), true);
    addField(builder, itemDefId, 'description', buildString(), false);
    addField(builder, itemDefId, 'rarity', buildString(), false);

    const inventoryItemId = createType(builder, 'InventoryItem', 'Instance of an owned item.');
    addEntityBaseFields(builder, inventoryItemId);
    addField(builder, inventoryItemId, 'itemId', buildReference(itemDefId), true);
    addField(builder, inventoryItemId, 'quantity', buildInteger(), true);

    const inventoryId = createType(builder, 'Inventory', 'Collection of player items.');
    addEntityBaseFields(builder, inventoryId);
    addField(builder, inventoryId, 'items', buildArray(buildTypeRef(inventoryItemId)), true);

    if (playerTypeId) {
      addField(builder, playerTypeId, 'inventory', buildTypeRef(inventoryId), false);
    }
  }

  if (enabledModules.has('clans')) {
    const clanId = createType(builder, 'Clan', 'Player guild or clan.');
    addEntityBaseFields(builder, clanId);
    addField(builder, clanId, 'name', buildString(), true);
    if (playerTypeId) {
      addField(builder, clanId, 'leaderId', buildReference(playerTypeId), false);
      addField(builder, clanId, 'memberIds', buildArray(buildReference(playerTypeId)), true);
    } else {
      addField(builder, clanId, 'memberIds', buildArray(buildString()), true);
    }
  }

  if (enabledModules.has('leaderboards')) {
    const entryId = createType(builder, 'LeaderboardEntry', 'Leaderboard entry.');
    addEntityBaseFields(builder, entryId);
    if (playerTypeId) {
      addField(builder, entryId, 'playerId', buildReference(playerTypeId), true);
    } else {
      addField(builder, entryId, 'playerId', buildString(), true);
    }
    addField(builder, entryId, 'score', buildNumber(), true);

    const boardId = createType(builder, 'Leaderboard', 'Leaderboard container.');
    addEntityBaseFields(builder, boardId);
    addField(builder, boardId, 'name', buildString(), true);
    addField(builder, boardId, 'entries', buildArray(buildTypeRef(entryId)), true);
  }

  if (enabledModules.has('matchmaking')) {
    const matchId = createType(builder, 'Match', 'Match session data.');
    addEntityBaseFields(builder, matchId);
    if (playerTypeId) {
      addField(builder, matchId, 'playerIds', buildArray(buildReference(playerTypeId)), true);
    } else {
      addField(builder, matchId, 'playerIds', buildArray(buildString()), true);
    }
    const statusEnumId = createEnum(builder, 'MatchStatus', ['pending', 'active', 'completed']);
    addField(builder, matchId, 'status', buildEnumRef(statusEnumId), true);
  }

  if (enabledModules.has('battlegrounds')) {
    const battlegroundId = createType(builder, 'Battleground', 'Battleground definition.');
    addEntityBaseFields(builder, battlegroundId);
    addField(builder, battlegroundId, 'name', buildString(), true);
    addField(builder, battlegroundId, 'maxPlayers', buildInteger(), false);
  }

  if (enabledModules.has('auction')) {
    const listingId = createType(builder, 'AuctionListing', 'Auction listing.');
    addEntityBaseFields(builder, listingId);
    if (playerTypeId) {
      addField(builder, listingId, 'sellerId', buildReference(playerTypeId), true);
    } else {
      addField(builder, listingId, 'sellerId', buildString(), true);
    }
    const itemDefId = builder.typeIds['ItemDefinition'];
    if (itemDefId) {
      addField(builder, listingId, 'itemId', buildReference(itemDefId), true);
    } else {
      addField(builder, listingId, 'itemId', buildString(), true);
    }
    addField(builder, listingId, 'price', buildNumber(), true);
  }

  if (enabledModules.has('chat')) {
    const messageId = createType(builder, 'ChatMessage', 'Chat message data.');
    addEntityBaseFields(builder, messageId);
    if (playerTypeId) {
      addField(builder, messageId, 'authorId', buildReference(playerTypeId), true);
    } else {
      addField(builder, messageId, 'authorId', buildString(), true);
    }
    addField(builder, messageId, 'body', buildString(), true);
    addField(builder, messageId, 'sentAt', buildString(), false);
  }

  if (enabledModules.has('quests')) {
    const questId = createType(builder, 'QuestDefinition', 'Quest configuration.');
    addEntityBaseFields(builder, questId);
    addField(builder, questId, 'title', buildString(), true);
    addField(builder, questId, 'description', buildString(), false);
    addField(builder, questId, 'requiredLevel', buildInteger(), false);
    addField(builder, questId, 'rewardAmount', buildNumber(), false);
  }

  if (enabledModules.has('crafting')) {
    const recipeId = createType(builder, 'CraftingRecipe', 'Crafting recipe definition.');
    addEntityBaseFields(builder, recipeId);
    addField(builder, recipeId, 'name', buildString(), true);

    const inputId = createType(builder, 'RecipeInput', 'Crafting input item.');
    addEntityBaseFields(builder, inputId);
    const outputId = createType(builder, 'RecipeOutput', 'Crafting output item.');
    addEntityBaseFields(builder, outputId);

    const itemDefId = builder.typeIds['ItemDefinition'];
    const itemSchema = itemDefId ? buildReference(itemDefId) : buildString();
    addField(builder, inputId, 'itemId', itemSchema, true);
    addField(builder, inputId, 'quantity', buildInteger(), true);
    addField(builder, outputId, 'itemId', itemSchema, true);
    addField(builder, outputId, 'quantity', buildInteger(), true);

    addField(builder, recipeId, 'inputs', buildArray(buildTypeRef(inputId)), true);
    addField(builder, recipeId, 'outputs', buildArray(buildTypeRef(outputId)), true);
  }

  const schema: JsonSchema = {
    $schema: SCHEMA_URI,
    $defs: builder.defs,
  };

  return normalizeSchema(schema);
};
